import { createClient } from '@supabase/supabase-js';
import dayjs from 'dayjs';
import {
  ChartsByOperation,
  SummariesByOperation,
  TestRun,
  TestRunOperation,
  TestRunSummary,
  TestScenarioSummary,
  TimeAggregationLevels,
} from 'interfaces/testRun';
const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_API_URL,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_TOKEN
);

export const getTestRun = async (
  token: string,
  testRunId: string
): Promise<TestRun> => {
  supabase.auth.setAuth(token);
  const { data, error } = await supabase
    .from<TestRun>('perf_test_runs')
    .select(
      'id,name,testScenarioId:test_scenario_id,publishStrategy:publish_strategy,startedAt:started_at,stoppedAt:stopped_at,createdAt:created_at'
    )
    .eq('id', testRunId);

  if (error || !data || data.length === 0) {
    throw error;
  }

  return data[0];
};

const shouldGetRawValues = (testRun: TestRun) => {
  return (
    testRun.publishStrategy === 'listener' &&
    (!testRun.stoppedAt ||
      dayjs().valueOf() - 60 * 1000 < dayjs(testRun.stoppedAt).valueOf())
  );
};

export const tryGetTestRunSummary = async (
  token: string,
  testRun: TestRun,
  operationNames: string[]
): Promise<SummariesByOperation> => {
  if (shouldGetRawValues(testRun)) {
    return getTestRunSummaryRaw(token, testRun.id, operationNames);
  } else {
    return getTestRunSummary(token, testRun.id, operationNames);
  }
};

export const getTestRunSummaryRaw = async (
  token: string,
  testRunId: string,
  operationNames: string[]
): Promise<SummariesByOperation> => {
  supabase.auth.setAuth(token);
  const operations =
    operationNames.length === 1 && operationNames[0] === 'Overall'
      ? null
      : operationNames;

  const { data, error } = await supabase
    .rpc('test_run_summaries_raw', { operations })
    .eq('runId', testRunId)
    .order('requests', { ascending: false });

  if (error) {
    throw error;
  }

  return data.reduce((acc, curr) => {
    acc[curr.operationName] = {
      id: testRunId,
      requestsPerSecond: parseFloat(
        (curr.requests / (curr.stoppedAt - curr.startedAt)).toFixed(5)
      ),
      failureRate: parseFloat((curr.failures / curr.requests).toFixed(5)),
      ...curr,
    };
    return acc;
  }, {});
};

export const getTestRunSummary = async (
  token: string,
  testRunId: string,
  operationNames: string[]
): Promise<SummariesByOperation> => {
  supabase.auth.setAuth(token);
  const { data, error } = await supabase
    .rpc('test_run_summaries')
    .eq('runId', testRunId)
    .in('operationName', operationNames)
    .order('requests', { ascending: false });

  if (error) {
    throw error;
  }

  return data.reduce((acc, curr) => {
    acc[curr.operationName] = {
      id: testRunId,
      requestsPerSecond: parseFloat(
        (curr.requests / (curr.stoppedAt - curr.startedAt)).toFixed(5)
      ),
      failureRate: parseFloat((curr.failures / curr.requests).toFixed(5)),
      ...curr,
    };
    return acc;
  }, {});
};

export const tryGetTestRunOperations = async (
  token: string,
  testRun: TestRun
): Promise<TestRunOperation[]> => {
  if (shouldGetRawValues(testRun)) {
    return getTestRunOperationsRaw(token, testRun.id);
  } else {
    return getTestRunOperations(token, testRun.id);
  }
};

export const getTestRunOperationsRaw = async (
  token: string,
  testRunId: string
): Promise<TestRunOperation[]> => {
  supabase.auth.setAuth(token);

  const { data, error } = await supabase
    .rpc('test_run_operations_raw')
    .eq('runId', testRunId);

  if (error) {
    throw error;
  }

  return data;
};

export const getTestRunOperations = async (
  token: string,
  testRunId: string
): Promise<TestRunOperation[]> => {
  supabase.auth.setAuth(token);
  const { data, error } = await supabase.rpc('get_test_run_operations', {
    run_id: testRunId,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const getOperationsByScenario = async (
  token: string,
  testScenarioId: string
): Promise<TestRunOperation[]> => {
  supabase.auth.setAuth(token);

  const { data, error } = await supabase
    .from<TestRunOperation & { test_scenario_id: string }>(
      'perf_test_operations'
    )
    .select('id,name')
    .eq('test_scenario_id', testScenarioId);

  if (error) {
    throw error;
  }

  return data;
};

export const tryGetTestRunCharts = async (
  token: string,
  testRun: TestRun,
  operationNames: string[],
  timeAggregationLevel: string
): Promise<ChartsByOperation> => {
  if (testRun.publishStrategy === 'listener') {
    return getTestRunChartsRaw(
      token,
      testRun.id,
      operationNames,
      timeAggregationLevel
    );
  } else {
    return getTestRunCharts(
      token,
      testRun.id,
      operationNames,
      timeAggregationLevel
    );
  }
};

export const getTestRunChartsRaw = async (
  token: string,
  testRunId: string,
  operationNames: string[],
  timeAggregationLevel: string
): Promise<ChartsByOperation> => {
  supabase.auth.setAuth(token);
  const timeInterval = convertToTimeInterval(timeAggregationLevel);
  const operations =
    operationNames.length === 1 && operationNames[0] === 'Overall'
      ? null
      : operationNames;

  const { data, error } = await supabase.rpc('get_test_run_charts_v1', {
    run_id: testRunId,
    operations,
    time_interval: timeInterval,
  });

  if (error) {
    throw error;
  }

  return data.reduce((acc, curr) => {
    acc[curr.operationName] = {
      ...curr,
      requestsPerSecond: curr.timestamps.map((_, i) =>
        parseFloat(
          (curr.requests[i] / convertToSeconds(timeAggregationLevel)).toFixed(5)
        )
      ),
      failureRate: curr.timestamps.map((_, i) =>
        parseFloat((curr.failures[i] / curr.requests[i]).toFixed(5))
      ),
    };
    return acc;
  }, {});
};

export const getTestRunCharts = async (
  token: string,
  testRunId: string,
  operationNames: string[],
  timeAggregationLevel: string
): Promise<ChartsByOperation> => {
  supabase.auth.setAuth(token);
  const { data, error } = await supabase.rpc('get_test_run_charts', {
    run_id: testRunId,
    operations: operationNames,
    time_aggregation_level: timeAggregationLevel,
  });

  if (error) {
    throw error;
  }

  return data.reduce((acc, curr) => {
    acc[curr.operationName] = {
      ...curr,
      requestsPerSecond: curr.timestamps.map((_, i) =>
        parseFloat(
          (curr.requests[i] / convertToSeconds(timeAggregationLevel)).toFixed(5)
        )
      ),
      failureRate: curr.timestamps.map((_, i) =>
        parseFloat((curr.failures[i] / curr.requests[i]).toFixed(5))
      ),
    };
    return acc;
  }, {});
};

export const getRecentTestRunSummaries = async (
  token: string,
  limit: number,
  offset: number,
  scenarioId: string = undefined,
  ascending = false
): Promise<TestRunSummary[]> => {
  supabase.auth.setAuth(token);
  const query = supabase
    .rpc('test_run_summaries')
    .eq('operationName', 'Overall')
    .order('stoppedAt', { ascending })
    .range(offset, offset + limit);

  if (scenarioId) {
    query.eq('scenarioId', scenarioId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data.map((d) => ({
    ...d,
    requestsPerSecond: d.requests / (d.stoppedAt - d.startedAt),
    failureRate: d.failures / d.requests,
  }));
};

export const getPopularTestScenarios = async (
  token: string,
  limit: number,
  offset: number
): Promise<TestScenarioSummary[]> => {
  supabase.auth.setAuth(token);
  // TODO(bobsin): order this
  const { data, error } = await supabase
    .rpc('test_scenarios')
    .range(offset, offset + limit);

  if (error) {
    throw error;
  }

  return data;
};

const convertToTimeInterval = (timeAggregationLevel: string): string => {
  return {
    [TimeAggregationLevels.fiveSecond]: '5 seconds',
    [TimeAggregationLevels.thirtySecond]: '30 seconds',
    [TimeAggregationLevels.oneMinute]: '1 minute',
    [TimeAggregationLevels.fiveMinute]: '5 minutes',
    [TimeAggregationLevels.thirtyMinute]: '30 minutes',
  }[timeAggregationLevel];
};

const convertToSeconds = (timeAggregationLevel: string): number => {
  return {
    [TimeAggregationLevels.fiveSecond]: 5,
    [TimeAggregationLevels.thirtySecond]: 30,
    [TimeAggregationLevels.oneMinute]: 60,
    [TimeAggregationLevels.fiveMinute]: 300,
    [TimeAggregationLevels.thirtyMinute]: 1800,
  }[timeAggregationLevel];
};
